import { LogOut, RefreshCw } from 'lucide-react';
import { Button } from './ui';
import errorIllustration from '../../../assets/error-illustration.png';

interface PremiumErrorScreenProps {
    title?: string;
    message?: string;
    onRetry?: () => void;
    onLogout?: () => void;
    isFullPage?: boolean;
}

const PremiumErrorScreen: React.FC<PremiumErrorScreenProps> = ({
    title = "Oops! Something went wrong",
    message = "We've encountered an unexpected error. Please try again later.",
    onRetry,
    onLogout,
    isFullPage = true,
}) => {
    const handleRetry = onRetry || (() => window.location.reload());

    return (
        <div className={`flex flex-col items-center justify-center w-full px-6 text-center bg-white overflow-hidden
      ${isFullPage ? 'h-[calc(100vh-140px)]' : 'min-h-[300px] py-6'}`}>

            <div className="flex flex-col items-center justify-center w-full max-w-lg">
                {/* Graphic Container - Scaled down for tighter fit */}
                <div className="relative w-48 h-48 tablet:w-64 tablet:h-64 mb-4 flex justify-center items-center">
                    <img
                        src={errorIllustration}
                        alt="Error illustration"
                        className="w-full h-full object-contain drop-shadow-lg"
                    />
                </div>

                {/* Text Content */}
                <div className="flex flex-col gap-1 mb-6">
                    <h1 className="text-xl tablet:text-2xl bricolage-grotesque-bold text-gray-900 leading-tight">
                        {title}
                    </h1>
                    <p className="text-gray-500 manrope-medium text-sm tablet:text-base max-w-sm mx-auto">
                        {message}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col items-center gap-3 w-full max-w-[180px]">
                    <Button
                        variant="primary"
                        className="w-full !h-[48px] !rounded-xl font-bold shadow-lg shadow-teal-primary/20"
                        onClick={handleRetry}
                    >
                        <RefreshCw size={16} className="mr-2" />
                        Try Again
                    </Button>
                    {onLogout && (
                        <Button
                            variant="ghost"
                            className="w-full !h-[40px] !rounded-xl text-gray-500 hover:text-red-500"
                            onClick={onLogout}
                        >
                            <LogOut size={15} className="mr-2" />
                            Log Out
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PremiumErrorScreen;
